/**
 * Main calendar onboarding flow
 *
 * Orchestrates the 3-step setup process:
 * 1. Permission — Request device calendar access
 * 2. Native Calendar Reminder — Check device calendar settings
 * 3. Your Calendars — Select which calendars belong to the user; this
 *    selection is also used as the initial FimFam visibility filter.
 */

import { useState, useEffect } from 'react';
import { Modal, Pressable, StyleSheet, View } from 'react-native';
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withTiming,
} from 'react-native-reanimated';
import { SafeAreaView } from 'react-native-safe-area-context';

import { Colors } from '@/constants/theme';
import { useColorScheme } from '@/hooks/use-color-scheme';
import type { DeviceCalendar } from '@/lib/calendar/DeviceCalendarService';

import { OnboardingStepIndicator } from './OnboardingStepIndicator';
import { PermissionStep } from './steps/PermissionStep';
import { NativeCalendarReminderStep } from './steps/NativeCalendarReminderStep';
import { LinkCalendarsToSelfStep } from './steps/LinkCalendarsToSelfStep';

type OnboardingStep =
  | 'permission'
  | 'nativeReminder'
  | 'linkToSelf';

const STEP_ORDER: OnboardingStep[] = [
  'permission',
  'nativeReminder',
  'linkToSelf',
];

const TOTAL_STEPS = STEP_ORDER.length;

type CalendarOnboardingFlowProps = {
  visible: boolean;
  calendars: DeviceCalendar[];
  preSelectedSelfCalendarIds?: string[];
  onComplete: (linkedCalendarIds: string[], visibleCalendars: DeviceCalendar[]) => void;
  onSkip?: () => void;
};

export function CalendarOnboardingFlow({
  visible,
  calendars,
  preSelectedSelfCalendarIds = [],
  onComplete,
  onSkip,
}: CalendarOnboardingFlowProps) {
  const colorScheme = useColorScheme();
  const palette = Colors[colorScheme ?? 'light'];

  const [currentStep, setCurrentStep] = useState<OnboardingStep>('permission');

  // Animation values
  const backdropOpacity = useSharedValue(0);
  const translateY = useSharedValue(500);

  useEffect(() => {
    if (visible) {
      backdropOpacity.value = withTiming(1, { duration: 200 });
      translateY.value = withTiming(0, { duration: 250 });
    } else {
      backdropOpacity.value = withTiming(0, { duration: 200 });
      translateY.value = withTiming(500, { duration: 200 });
    }
  }, [visible, backdropOpacity, translateY]);

  const backdropStyle = useAnimatedStyle(() => ({
    opacity: backdropOpacity.value,
  }));

  const contentStyle = useAnimatedStyle(() => ({
    transform: [{ translateY: translateY.value }],
  }));

  const currentStepIndex = STEP_ORDER.indexOf(currentStep);

  const goToStep = (step: OnboardingStep) => {
    setCurrentStep(step);
  };

  const handlePermissionGranted = () => {
    goToStep('nativeReminder');
  };

  const handleNativeReminderContinue = () => {
    goToStep('linkToSelf');
  };

  const handleLinkedCalendars = (selectedIds: string[]) => {
    const selectedSet = new Set(selectedIds);
    const visibleCalendars = calendars.filter((c) => selectedSet.has(c.id));
    setCurrentStep('permission');
    onComplete(selectedIds, visibleCalendars);
  };

  const handleSkip = () => {
    setCurrentStep('permission');
    onSkip?.();
  };

  return (
    <Modal
      visible={visible}
      animationType="none"
      transparent
      onRequestClose={handleSkip}
      statusBarTranslucent
    >
      <View style={styles.overlay}>
        {/* Animated Backdrop */}
        <Animated.View style={[styles.backdrop, backdropStyle]}>
          <Pressable style={StyleSheet.absoluteFill} onPress={handleSkip} />
        </Animated.View>

        {/* Animated Content */}
        <Animated.View
          style={[
            styles.content,
            { backgroundColor: palette.background },
            contentStyle,
          ]}
        >
          <SafeAreaView style={styles.container}>
            <OnboardingStepIndicator
              currentStep={currentStepIndex + 1}
              totalSteps={TOTAL_STEPS}
            />

            {currentStep === 'permission' && (
              <PermissionStep
                onContinue={handlePermissionGranted}
                onSkip={onSkip}
              />
            )}

            {currentStep === 'nativeReminder' && (
              <NativeCalendarReminderStep
                onContinue={handleNativeReminderContinue}
                onSkip={onSkip}
              />
            )}

            {currentStep === 'linkToSelf' && (
              <LinkCalendarsToSelfStep
                calendars={calendars}
                preSelectedIds={preSelectedSelfCalendarIds}
                onContinue={handleLinkedCalendars}
                onBack={() => goToStep('nativeReminder')}
              />
            )}
          </SafeAreaView>
        </Animated.View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    justifyContent: 'flex-end',
  },
  backdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  content: {
    height: '95%',
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    overflow: 'hidden',
  },
  container: {
    flex: 1,
  },
});
